import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	total: 0,
	travel: null,
	loading: false,
	error: null,
};

// Load all travel requests
export const loadAllTravel = createAsyncThunk(
	"travel/loadAllTravel",
	async ({ page = 1, limit = 10, approvalStatus } = {}) => {
		try {
			const params = new URLSearchParams();
			if (page) params.append("page", page);
			if (limit) params.append("limit", limit);
			if (approvalStatus) params.append("approvalStatus", approvalStatus);

			const { data } = await axios.get(`travel?${params.toString()}`);
			return data;
		} catch (error) {
			// console.log(error.message);
			return { list: [], total: 0 };
		}
	}
);

// Add travel request
export const addTravel = createAsyncThunk(
	"travel/addTravel",
	async (values) => {
		try {
			const { data } = await axios({
				method: "post",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				url: `travel/`,
				data: {
					...values,
				},
			});
			toast.success("Travel Request Created");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in creating travel request, try again!");
			return {
				message: "error",
			};
		}
	}
);

// Update travel request
export const updateTravel = createAsyncThunk(
	"travel/updateTravel",
	async ({ id, values }) => {
		try {
			const { data } = await axios({
				method: "put",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				url: `travel/${id}`,
				data: {
					...values,
				},
			});
			toast.success("Travel Request Updated");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in updating travel request, try again!");
			return {
				message: "error",
			};
		}
	}
);

// Delete travel request
export const deleteTravel = createAsyncThunk(
	"travel/deleteTravel",
	async (id) => {
		try {
			const { data } = await axios({
				method: "delete",
				url: `travel/${id}`,
			});
			toast.success("Travel Request Deleted");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in deleting travel request, try again!");
			return {
				message: "error",
			};
		}
	}
);

// Approve/Reject travel request
export const reviewTravel = createAsyncThunk(
	"travel/reviewTravel",
	async ({ id, values }) => {
		try {
			const { data } = await axios({
				method: "put",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				url: `travel/${id}/review`,
				data: {
					...values,
				},
			});
			toast.success("Travel Request Reviewed");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in reviewing travel request, try again!");
			return {
				message: "error",
			};
		}
	}
);

// Count travel requests
export const countTravel = createAsyncThunk("travel/countTravel", async () => {
	try {
		const { data } = await axios.get(`travel/count`);
		return data;
	} catch (error) {
		// console.log(error.message);
		return { total: 0 };
	}
});

const travelSlice = createSlice({
	name: "travel",
	initialState,
	reducers: {
		clearTravel: (state) => {
			state.travel = null;
		},
	},
	extraReducers: (builder) => {
		// Load all travel requests
		builder.addCase(loadAllTravel.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(loadAllTravel.fulfilled, (state, action) => {
			state.loading = false;
			state.list = Array.isArray(action.payload?.list)
				? action.payload.list
				: Array.isArray(action.payload)
				? action.payload
				: [];
			state.total = action.payload?.total || action.payload?.list?.length || 0;
		});
		builder.addCase(loadAllTravel.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error.message;
		});

		// Add travel request
		builder.addCase(addTravel.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(addTravel.fulfilled, (state, action) => {
			state.loading = false;
			if (action.payload.message === "success") {
				// Optionally refresh the list
			}
		});
		builder.addCase(addTravel.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error.message;
		});

		// Update travel request
		builder.addCase(updateTravel.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(updateTravel.fulfilled, (state, action) => {
			state.loading = false;
		});
		builder.addCase(updateTravel.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error.message;
		});

		// Delete travel request
		builder.addCase(deleteTravel.fulfilled, (state) => {
			// Optionally refresh the list
		});

		// Review travel request
		builder.addCase(reviewTravel.fulfilled, (state) => {
			// Optionally refresh the list
		});

		// Count travel requests
		builder.addCase(countTravel.fulfilled, (state, action) => {
			state.total = action.payload?.total || 0;
		});
	},
});

export default travelSlice.reducer;

